import { forwardRef, SelectHTMLAttributes, ReactNode } from 'react'
import { cn } from 'utils/cn'
import { inputClasses } from './Input'

interface SelectOption {
    value: string | number
    label: string
    disabled?: boolean
}

// `size` is deliberately omitted from the native attributes: on <select> it means "visible rows"
// and is typed `number`, which collides with the design-system size variant below. The native
// attribute has no use here — this control is always a closed dropdown.
interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'size'> {
    options: SelectOption[]
    label?: string
    error?: string
    helperText?: string
    required?: boolean
    placeholder?: string
    icon?: ReactNode
    onChange?: (value: string) => void
    /**
     * `md` (default) is the form field: full-width, `text-sm`, roomy padding.
     * `sm` is the toolbar/filter-bar variant — `h-8` and `text-xs` to sit level with
     * `Combobox`, which filter bars pair it with. Size only changes geometry; border,
     * focus ring and dark-mode colors stay shared with `inputClasses`.
     */
    size?: 'sm' | 'md'
}

/**
 * Geometry per size. `inputClasses` bakes in `px-3 py-2 text-sm`, so the `sm` variant genuinely
 * conflicts with the base — these are composed through `cn()` (tailwind-merge), which resolves
 * a conflicting pair by last-wins rather than leaving both in the class string for the CSS
 * cascade to arbitrate. Same mechanism the Button relies on for consumer overrides.
 */
const SIZE_CLASSES: Record<'sm' | 'md', { field: string; chevronInset: string; iconPad: string; iconInset: string }> = {
    // Padding is expressed as `px-*` first so it collides with the base `px-3` and tailwind-merge
    // drops the base — a `pl-*`/`px-*` pair is two DISTINCT utilities, which merge keeps both of,
    // leaving the winner to stylesheet source order. The one-sided `pr-*`/`pl-*` overrides come
    // after, and each collides only with its own side.
    sm: { field: 'h-8 py-0 px-2.5 pr-8 text-xs', chevronInset: 'bg-[position:right_8px_center]', iconPad: 'pl-7', iconInset: 'left-2' },
    md: { field: 'px-3 pr-10', chevronInset: 'bg-[position:right_12px_center]', iconPad: 'pl-9', iconInset: 'left-3' },
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
    ({ options, label, error, helperText, required, placeholder, icon, onChange, className = '', value, size = 'md', ...props }, ref) => {
        const sizing = SIZE_CLASSES[size]
        return (
            <div className={size === 'sm' ? '' : 'space-y-1.5'}>
                {label && (
                    <label className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {label}
                        {required && <span className="ml-0.5 text-red-500">*</span>}
                    </label>
                )}
                <div className="relative">
                    {icon && (
                        <div className={`pointer-events-none absolute ${sizing.iconInset} top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500`}>
                            {icon}
                        </div>
                    )}
                    <select
                        ref={ref}
                        value={value}
                        onChange={(e) => onChange?.(e.target.value)}
                        className={cn(
                            inputClasses,
                            'appearance-none bg-[length:16px_16px] bg-no-repeat',
                            sizing.field,
                            sizing.chevronInset,
                            icon && sizing.iconPad,
                            error && 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
                            className
                        )}
                        style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                        }}
                        {...props}
                    >
                        {placeholder && (
                            <option value="" disabled>
                                {placeholder}
                            </option>
                        )}
                        {options.map((opt) => (
                            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>
                {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
                {helperText && !error && <p className="text-xs text-slate-500 dark:text-slate-400">{helperText}</p>}
            </div>
        )
    }
)

Select.displayName = 'Select'
